<script setup lang="ts">
import { computed, ref, onMounted, onBeforeUnmount } from 'vue'
import { storeToRefs } from 'pinia'
import { useGnbStore } from '~/store/layout/gnb'
import { defineEmits } from 'vue'

/**
 * @params select - 메뉴 선택 시 발생하는 이벤트
 * @author 프론트엔드팀
 * @see 모바일 GNB 컴포넌트 (a-drawer 내부에서 사용)
 */
const emit = defineEmits<{
  (e: 'select'): void
}>()

const { firstdepth } = storeToRefs(useGnbStore())

// 화면 크기 상태 관리
const windowWidth = ref(0)

/**
 * 현재 화면이 모바일인지 체크
 * @returns boolean - 1199px 이하이면 true
 */
const isMobile = computed(() => windowWidth.value <= 1199)

/**
 * SNB에 표시할 메뉴 아이템 필터링
 * @see 데스크탑: showgnb !== false인 메뉴만 표시
 * @see 모바일: showgnb !== false 또는 id가 5(사이트맵)인 메뉴 표시
 */
const snbItems = computed(() => {
  if (isMobile.value) {
    // 모바일: showgnb가 false가 아니거나 사이트맵(id: 5)인 경우 표시
    return firstdepth.value.filter(item =>
        item.showgnb !== false || item.id === 5
    )
  } else {
    // 데스크탑: showgnb가 false가 아닌 경우만 표시
    return firstdepth.value.filter(item => item.showgnb !== false)
  }
})

/**
 * 메뉴 선택 시 Drawer를 닫기 위한 이벤트 발생
 */
function onSelect() {
  emit('select')
}

/**
 * 외부 링크 여부 체크
 * @params url - 체크할 URL
 * @returns boolean - http:// 또는 https://로 시작하면 true
 */
function isExternal(url: string): boolean {
  return /^https?:\/\//.test(url)
}

/**
 * 화면 크기 업데이트 핸들러
 */
function updateWidth() {
  windowWidth.value = window.innerWidth
}

onMounted(() => {
  updateWidth()
  window.addEventListener('resize', updateWidth)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateWidth)
})
</script>

<template>
  <a-collapse expand-icon-position="end" class="snb-accordion">
    <a-collapse-panel
        v-for="item in snbItems"
        :key="item.id"
        :header="item.label"
    >
      <ul class="snb-list">
        <li
            v-for="child in item.children"
            :key="child.id"
        >
          <!-- 외부 링크 -->
          <NuxtLink
          v-if="isExternal(child.url)"
          :href="child.url"
          target="_blank"
          @click="onSelect"
          >
          {{ child.label }}
          </NuxtLink>
          <!-- 내부 라우팅 -->
          <NuxtLink
              v-else
              :to="child.url"
              @click="onSelect"
          >
            {{ child.label }}
          </NuxtLink>
        </li>
      </ul>
    </a-collapse-panel>
  </a-collapse>
</template>

<style scoped lang="scss">
</style>